// app/routes/$locale.tsx
import { Navigate, useLocation } from "react-router";
import type { Route } from "./+types/$locale";

import { Layout as SiteLayout } from "@/components/layout/Layout";
import { RouteLoading } from "@/components/RouteLoading";
import { useLocale } from "@/i18n/useLocale";
import {
  buildPublishedLocaleUrl,
  parseLocalePrefixedPath,
  rewritePathToPublishedLocale,
} from "@/i18n/routing";

export default function LocaleLayout(_props: Route.ComponentProps) {
  const location = useLocation();
  const localeState = useLocale();

  // Manifest still loads async in Phase 3 (Phase 6 makes it synchronous).
  // In practice LocaleProvider also renders <RouteLoading /> until ready,
  // so this guard is defensive — it will rarely (if ever) fire.
  if (!localeState) return <RouteLoading />;

  const { manifest, locale } = localeState;

  // Compatibility redirect: if the URL's locale doesn't match the user's
  // resolved locale (stale localStorage, manifest changed, etc.), rewrite.
  const targetUrl = rewritePathToPublishedLocale({
    manifest,
    to: `${location.pathname}${location.search}${location.hash}`,
    savedLocale: locale,
    host: typeof window === "undefined" ? "" : window.location.host,
  });
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  // Normalize trailing slash before comparing so `/en` and `/en/` aren't
  // treated as different. `buildPublishedLocaleUrl` always appends `/` for the
  // locale-root case (`/en/`), but the prerender input + browser URL is
  // typically `/en`. Without this normalization the locale layout would render
  // `<Navigate to="/en/" />` during prerender, producing an empty Suspense body.
  const stripTrailing = (u: string) =>
    u.length > 1 && u.endsWith("/") ? u.slice(0, -1) : u;
  if (stripTrailing(targetUrl) !== stripTrailing(currentUrl)) {
    return <Navigate to={targetUrl} replace />;
  }

  // Shell consistency: param locale must match resolved locale.
  const parsed = parseLocalePrefixedPath(location.pathname, [locale]);
  if (parsed.locale !== locale) {
    return (
      <Navigate
        to={buildPublishedLocaleUrl(
          locale,
          parsed.remainderPathname,
          location.search,
          location.hash,
        )}
        replace
      />
    );
  }

  // SiteLayout renders <Outlet /> internally to mount child routes.
  return <SiteLayout />;
}
